import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ShieldCheck, Landmark, Users, Scale, Building2, Briefcase } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const axiomEditions = [
  {
    name: "AXIOM for Underwriters",
    status: "LIVE",
    url: "https://underwriter.nwta.app/",
    icon: ShieldCheck,
    logo: "/assets/products/marks/axiom-underwriter-mark.png",
    span: "md:col-span-7",
    headline: "Underwriting-grade answers in any state.",
    copy: "The requirements to insure, written for the professional who has to clear the file.",
    points: [
      "Ask in any state — requirements to insure, sourced correctly the first time",
      "Escalate to counsel-grade or claims-grade depth in one click",
      "Your bulletins, notices, and proprietary guidelines searchable on demand",
    ],
  },
  {
    name: "AXIOM for Lenders",
    status: "LIVE",
    url: "https://lenders.nwta.app/",
    icon: Landmark,
    logo: "/assets/products/marks/axiom-lenders-mark.png",
    span: "md:col-span-5",
    headline: "Title risk and collateral exposure — instantly.",
    copy: "Underwriting-grade answers grounded in real ALTA endorsement language.",
    points: [
      "Ask, don't search — cited, not guessed, in actual ALTA endorsement text",
      "Upload a title report or loan doc, get flagged risk items instantly",
      "Draft client-ready emails for borrowers, sellers, agents, and attorneys",
    ],
  },
  {
    name: "AXIOM for Agents",
    status: "LIVE",
    url: "https://axiom.northwesttitlealliance.com/",
    icon: Users,
    logo: "/assets/products/marks/axiom-agents-mark.png",
    span: "md:col-span-5",
    headline: "The same underwriting brain, at your agency desk.",
    copy: "Delivered under Northwest Tech Alliance branding for agents beyond the Northwest.",
    points: [
      "Underwriting-grade answers for your agency desk in any state",
      "Escalate to counsel-grade / claims-grade depth when the file demands it",
      "Not a generic chatbot — built on how title professionals assess risk",
    ],
  },
  {
    name: "AXIOM for Attorneys",
    status: "LIVE",
    url: "https://attorney.nwta.app/",
    icon: Scale,
    logo: "/assets/products/marks/axiom-attorneys-mark.png",
    span: "md:col-span-7",
    headline: "Counsel-grade research depth.",
    copy: "Citations and reasoning framed for legal review — depth that holds up in the file.",
    points: [
      "Litigation-ready analysis, either side of the v. — elements, defenses, proof burdens",
      "Claims work and neutral research memos — fully cited, with edge cases flagged",
      "Underwriting posture: what a title insurer would actually insure, decision-first",
    ],
  },
  {
    name: "AXIOM for National Agencies",
    status: "COMING SOON",
    icon: Building2,
    logo: "/assets/products/marks/axiom-national-mark.png",
    span: "md:col-span-6",
    headline: "Every market you operate in.",
    copy: "Consistent, state-specific underwriting answers across your entire footprint.",
    points: [],
  },
  {
    name: "AXIOM for Commercial Transactions",
    status: "COMING SOON",
    icon: Briefcase,
    logo: "/assets/products/marks/axiom-commercial-mark.png",
    span: "md:col-span-6",
    headline: "Tuned for complex transactions.",
    copy: "Underwriting-grade answers for commercial deals and endorsements.",
    points: [],
  },
];

export const StatusPill = ({ status }) =>
  status === "LIVE" ? (
    <span className="inline-flex items-center gap-1.5 font-mono-accent text-[10px] tracking-[0.2em] px-2.5 py-1 border border-live/50 text-live" data-testid="status-pill-live">
      <span className="h-1.5 w-1.5 rounded-full bg-live animate-pulse" /> LIVE
    </span>
  ) : (
    <span className="inline-flex items-center font-mono-accent text-[10px] tracking-[0.2em] px-2.5 py-1 border border-steel/60 text-fog/40" data-testid="status-pill-coming-soon">
      COMING SOON
    </span>
  );

export const ProductCard = ({ p, index }) => {
  const Icon = p.icon;
  const inner = (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.1, ease: EASE }}
      className={`group relative h-full border border-steel/30 bg-navy-deep/60 p-6 md:p-8 flex flex-col overflow-hidden transition-colors duration-500 ${
        p.status === "LIVE" ? "hover:border-tech/70" : "opacity-80 hover:opacity-100 hover:border-steel/60"
      }`}
      data-testid={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {p.logo && (
        <img
          src={p.logo}
          alt=""
          aria-hidden="true"
          loading="lazy"
          data-testid={`product-mark-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`absolute -top-3 -right-3 w-24 md:w-28 pointer-events-none select-none transition-opacity duration-500 ${
            p.logoDark
              ? "opacity-[0.55] group-hover:opacity-[0.80]"
              : "opacity-[0.35] group-hover:opacity-[0.60]"
          }`}
        />
      )}
      <div className="absolute top-0 left-0 h-[2px] w-0 bg-tech group-hover:w-full transition-[width] duration-700 ease-out" />
      <div className="relative flex items-start justify-between mb-6">
        <Icon className={`h-6 w-6 ${p.status === "LIVE" ? "text-tech-bright" : "text-steel"}`} strokeWidth={1.5} />
        <StatusPill status={p.status} />
      </div>
      <h3 className="font-display font-semibold tracking-tight text-xl md:text-2xl text-fog mb-2">{p.name}</h3>
      <p className="font-display text-sm md:text-base text-tech-bright/90 mb-2.5">{p.headline}</p>
      <p className="text-[13px] text-fog/60 leading-relaxed mb-5">{p.copy}</p>
      {p.points.length > 0 && (
        <ul className="mt-auto space-y-2 mb-5">
          {p.points.map((pt) => (
            <li key={pt} className="flex gap-3 text-[13px] text-fog/70 leading-relaxed">
              <span className="mt-[6px] h-1 w-1 shrink-0 rotate-45 bg-tech" />
              {pt}
            </li>
          ))}
        </ul>
      )}
      {p.url && (
        <span className="mt-auto inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.22em] uppercase text-fog/50 group-hover:text-tech-bright transition-colors duration-300">
          Launch product
          <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
        </span>
      )}
    </motion.article>
  );

  return p.url ? (
    <a href={p.url} target="_blank" rel="noopener noreferrer" className={`${p.span} block`} data-testid={`product-link-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {inner}
    </a>
  ) : (
    <div className={p.span}>{inner}</div>
  );
};

const families = [
  {
    name: "The AXIOM Family",
    tagline: "One underwriting brain. Every desk.",
    blurb: "Six purpose-built editions of the same underwriting intelligence — for underwriters, lenders, agents, attorneys, national agencies, and commercial teams.",
    stat: "4 editions live · 2 coming soon",
    mixed: true,
    href: "/axiom",
    cta: "Explore AXIOM",
    logo: "/assets/products/marks/axiom-underwriter-mark.png",
  },
  {
    name: "TrueNorth",
    tagline: "ALTA/NSPS survey underwriting intelligence.",
    blurb: "Drop in a title commitment — TrueNorth checks closure, plots the legal, and grades every exception against 2021 ALTA/NSPS Table A.",
    stat: "Live · Nationwide coverage",
    href: "/truenorth",
    cta: "Explore TrueNorth",
    logo: "/assets/products/marks/truenorth-mark.png",
    logoDark: true,
  },
  {
    name: "ART",
    tagline: "AI Rendered Title — an AI-powered title plant.",
    blurb: "OIC-compliant and back to State Patent — title searches in minutes instead of days, rolling out county by county.",
    stat: "Live · OIC-compliant",
    href: "/art",
    cta: "Explore ART",
    logo: "/assets/products/marks/art-mark.png",
    logoDark: true,
  },
];

const FamilyCard = ({ f, index }) => (
  <Link
    to={f.href}
    className="md:col-span-4 block"
    data-testid={`family-card-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: EASE }}
      className="group relative h-full min-h-[340px] border border-steel/30 bg-navy-deep/60 p-7 md:p-9 flex flex-col overflow-hidden hover:border-tech/70 transition-colors duration-500"
    >
      <img
        src={f.logo}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className={`absolute -top-4 -right-4 w-32 md:w-36 pointer-events-none select-none transition-opacity duration-500 ${
          f.logoDark ? "opacity-[0.50] group-hover:opacity-[0.75]" : "opacity-[0.45] group-hover:opacity-[0.70]"
        }`}
      />
      <div className="absolute top-0 left-0 h-[2px] w-0 bg-tech group-hover:w-full transition-[width] duration-700 ease-out" />
      <span
        className={`relative inline-flex self-start items-center gap-1.5 font-mono-accent text-[10px] tracking-[0.2em] px-2.5 py-1 border ${
          f.mixed ? "border-tech/50 text-tech-bright" : "border-live/50 text-live"
        }`}
      >
        {!f.mixed && <span className="h-1.5 w-1.5 rounded-full bg-live animate-pulse" />} {f.stat}
      </span>
      <h3 className="relative font-display font-bold tracking-tight text-2xl md:text-3xl text-fog mt-8">{f.name}</h3>
      <p className="font-display text-sm md:text-base text-tech-bright/90 mt-2">{f.tagline}</p>
      <p className="text-[13.5px] text-fog/60 leading-relaxed mt-4 mb-8">{f.blurb}</p>
      <span className="mt-auto inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.22em] uppercase text-fog/50 group-hover:text-tech-bright transition-colors duration-300">
        {f.cta}
        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
      </span>
    </motion.article>
  </Link>
);

export const Products = () => (
  <section id="products" className="relative bg-navy py-24 md:py-36" data-testid="products-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24"
      >
        <div className="md:col-span-3">
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright">01 / Products</span>
        </div>
        <div className="md:col-span-9">
          <h2 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-fog">
            Three products<span className="text-tech">.</span>
            <br />
            <span className="text-fog/40">One mission: arm the industry.</span>
          </h2>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {families.map((f, i) => (
          <FamilyCard key={f.name} f={f} index={i} />
        ))}
      </div>
    </div>
  </section>
);
